import type { SVGProps } from 'react';
import {
  Shield,
  Lock,
  Zap,
  Globe,
  Server,
  Gem,
  Eye,
  EyeOff,
  Route,
  Cpu,
  Check,
  ChevronRight,
  Download,
  GitBranch,
  Terminal,
  BookOpen,
  Plus,
  Trash2,
  Pencil,
  Settings,
  Mail,
  Bell,
  Users,
  User,
  CreditCard,
  Archive,
  Upload,
  FileText,
  Activity,
  Bot,
  LogOut,
  Search,
  RefreshCw,
  Quote,
  Star,
  ShieldAlert,
  ShieldCheck,
  Ban,
  ArrowRight,
  ArrowLeft,
  X,
  Send,
} from 'lucide-react';

// Re-export lucide icons with the project's Icon* naming — all existing imports keep working.
// lucide props (size, className, strokeWidth, etc.) are forwarded as-is.
export const IconShield = Shield;
export const IconLock = Lock;
export const IconZap = Zap;
export const IconGlobe = Globe;
export const IconServer = Server;
export const IconGem = Gem;
export const IconEye = Eye;
export const IconEyeOff = EyeOff;
export const IconRoute = Route;
export const IconCpu = Cpu;
export const IconCheck = Check;
export const IconChev = ChevronRight;
export const IconDl = Download;
export const IconGit = GitBranch;
export const IconTerm = Terminal;

// ── Brand/platform icons ──────────────────────────────────────────────
// lucide ships no platform brand logos, so these are inline SVGs. They take
// the same width/height/className props as lucide icons and use currentColor
// so they inherit text color. Filled marks read correctly at small sizes.
type SvgProps = SVGProps<SVGSVGElement> & { width?: number | string; height?: number | string };

/** Windows — the four-pane logo. */
export function IconWindows({ width = 24, height = 24, ...p }: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" width={width} height={height} fill="currentColor" aria-hidden {...p}>
      <path d="M3 5.4 10.4 4.4v7.1H3zM11.3 4.28 21 3v8.5h-9.7zM3 12.5h7.4v7.1L3 18.6zM11.3 12.5H21V21l-9.7-1.28z" />
    </svg>
  );
}

/** Android — the robot head. */
export function IconAndroid({ width = 24, height = 24, ...p }: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" width={width} height={height} fill="currentColor" aria-hidden {...p}>
      <path d="M6 8.5 4.6 6a.5.5 0 0 1 .87-.5L6.9 8a7.3 7.3 0 0 1 10.2 0l1.43-2.5a.5.5 0 0 1 .87.5L18 8.5A6.9 6.9 0 0 1 21 14H3a6.9 6.9 0 0 1 3-5.5ZM8.2 12.1a.95.95 0 1 0 0-1.9.95.95 0 0 0 0 1.9Zm7.6 0a.95.95 0 1 0 0-1.9.95.95 0 0 0 0 1.9Z" />
      <rect x="3" y="14.6" width="18" height="6.4" rx="2" />
    </svg>
  );
}

/** Apple — the filled brand mark. */
export function IconApple({ width = 24, height = 24, ...p }: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" width={width} height={height} fill="currentColor" aria-hidden {...p}>
      <path d="M16.36 12.5c.03 2.9 2.54 3.86 2.57 3.87-.02.07-.4 1.37-1.32 2.71-.8 1.16-1.63 2.31-2.93 2.34-1.28.02-1.69-.76-3.15-.76s-1.92.73-3.13.78c-1.26.05-2.22-1.25-3.02-2.4-1.64-2.37-2.9-6.7-1.21-9.62.84-1.45 2.34-2.37 3.96-2.4 1.24-.02 2.4.83 3.16.83.75 0 2.17-1.03 3.66-.88.62.03 2.37.25 3.5 1.9-.09.06-2.09 1.22-2.06 3.63ZM14 4.9c.67-.81 1.12-1.94.99-3.06-.96.04-2.13.64-2.82 1.45-.62.72-1.16 1.87-1.02 2.97 1.07.08 2.17-.55 2.85-1.36Z" />
    </svg>
  );
}

/** Linux — Tux, simplified. */
export function IconLinux({ width = 24, height = 24, ...p }: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" width={width} height={height} fill="currentColor" aria-hidden {...p}>
      <path d="M12 2c2 0 3.3 1.7 3.3 3.9 0 1.3.4 2 1.3 3.1 1.4 1.6 2.6 3 3 5 .3 1.4-.1 2.3-.9 2.5-.5.1-1-.1-1.4-.5-.2 1.5-.8 3-1.9 3.9-1 .8-2.2 1.1-3.4 1.1s-2.4-.3-3.4-1.1c-1.1-.9-1.7-2.4-1.9-3.9-.4.4-.9.6-1.4.5-.8-.2-1.2-1.1-.9-2.5.4-2 1.6-3.4 3-5 .9-1.1 1.3-1.8 1.3-3.1C8.7 3.7 10 2 12 2Zm-1.6 4.2c-.5 0-.8.4-.8.9s.3.9.8.9.8-.4.8-.9-.3-.9-.8-.9Zm3.2 0c-.5 0-.8.4-.8.9s.3.9.8.9.8-.4.8-.9-.3-.9-.8-.9Zm-1.6 2.3c-.7 0-1.6.4-1.6.9 0 .3.7.7 1.6.7s1.6-.4 1.6-.7c0-.5-.9-.9-1.6-.9Z" />
    </svg>
  );
}
export const IconBook = BookOpen;
export const IconPlus = Plus;
export const IconTrash = Trash2;
export const IconEdit = Pencil;
export const IconSettings = Settings;
export const IconMail = Mail;
export const IconBell = Bell;
export const IconUsers = Users;
export const IconUser = User;
export const IconCreditCard = CreditCard;
export const IconArchive = Archive;
export const IconUpload = Upload;
export const IconFileText = FileText;
export const IconActivity = Activity;
export const IconBot = Bot;
export const IconLogOut = LogOut;
export const IconSearch = Search;
export const IconRefresh = RefreshCw;
export const IconQuote = Quote;
export const IconStar = Star;
export const IconShieldAlert = ShieldAlert;
export const IconShieldCheck = ShieldCheck;
export const IconBan = Ban;
export const IconArrowRight = ArrowRight;
export const IconArrowLeft = ArrowLeft;
export const IconX = X;
export const IconSend = Send;
